<template>
  <div>
    <a-table 
      :columns="visibleColumns"
      :data-source="list"
      :pagination="pagination"
      :rowClassName="inbreedingRowClass"
      @change="handleTableChange"
      @expand="onExpand"
    >
      <div slot="expandedRowRender" slot-scope="record" style="margin: 0" expandIcon="">
        <yu-zhong-chart-result :male_id="record.male_id" :female_id="record.female_id" :ref="`result-${record.male_vid}-${record.female_vid}`"></yu-zhong-chart-result>
      </div>
      <template slot="male_vid" slot-scope="male_id, row">
        <item-button :text="`${row.male_vid}`" :id="row.male_id"></item-button>
      </template>
      <template slot="female_vid" slot-scope="female_id, row">
        <item-button :text="`${row.female_vid}`" :id="row.female_id"></item-button>
      </template>
      <template slot="item_select_record_name" slot-scope="item_select_record_name, row">
        <a-tag v-if="row.item_select_record" class="select-record-tag" @click="goToSelectRecord(row.item_select_record.id)">
          {{ row.item_select_record.name }}
        </a-tag>
        <span v-else>-</span>
      </template>
      <template slot="predicted_offspring_f" slot-scope="predicted_offspring_f">
        <span :style="{ color: getInbreedingColor(predicted_offspring_f) }">
          {{ formatInbreeding(predicted_offspring_f) }}
        </span>
        <a-tooltip v-if="getInbreedingLevel(predicted_offspring_f) === 'warning'" placement="top">
          <template slot="title">
            注意，存在一定近交风险，请确保父本的育种值（EBV）足够优秀以覆盖风险
          </template>
          <a-tag color="orange" style="margin-left: 4px; font-size: 11px; cursor: pointer;">警告</a-tag>
        </a-tooltip>
        <a-tooltip v-if="getInbreedingLevel(predicted_offspring_f) === 'danger'" placement="top">
          <template slot="title">
            预测后代近交系数超过 6.25%，强烈不建议此配对
          </template>
          <a-tag color="red" style="margin-left: 4px; font-size: 11px; cursor: pointer;">禁止</a-tag>
        </a-tooltip>
      </template>
      <template slot="operation" slot-scope="scope, row">
        <a-dropdown>
          <a-menu slot="overlay" @click="(e) => handleItemAction(e, row)">
            <a-menu-item key="add_offspring"> <a-icon type="plus" />添加后代</a-menu-item>
            <a-menu-item key="remove"> <a-icon type="delete" />移除</a-menu-item>
          </a-menu>
          <a-button style="margin-left: 8px" size="small"> 操作 <a-icon type="down" /> </a-button>
        </a-dropdown>
      </template>
    </a-table>
    <!-- 添加后代弹窗 -->
    <breed-item-edit ref="breedItemEdit" @finish-edit="onOffspringAdded" />
  </div>
</template>

<script>
import ItemButton from '../components/ItemButton.vue'
import yuZhongChartResult from '../yu_zhong/components/yuZhongChartResult.vue'
import breedItemEdit from '../breed_items/components/addOrEdit.vue'
export default {
  name: "FarmCellRelationTable",
  components: {
    ItemButton, yuZhongChartResult, breedItemEdit
  },
  props: {
    // 额外的查询参数
    extraParams: {
      type: Object,
      default: () => ({})
    },
    // 是否显示近交系数列（由父组件传入近交模式状态）
    useInbreedingAlgo: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      list: [],
      query: {
        page_no: 1,
        page_size: 20,
        farm_id: undefined,
        start_at: undefined,
        end_at: undefined,
        item_select_record_id: undefined,
        farm_cell_id: undefined
      },
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
        showTotal: total => `共 ${total} 条记录`
      },
      columns: [
        {
          title: "Id",
          dataIndex: "id",
          key: "id",
        },
        {
          title: "雄性供体VId",
          dataIndex: "male_id",
          key: "male_id",
          scopedSlots: {customRender: 'male_vid'}
        },
        {
          title: "雌性供体VId",
          dataIndex: "female_id",
          key: "female_id",
          scopedSlots: {customRender: 'female_vid'}
        },
        {
          title: "预测后代近交系数",
          dataIndex: "predicted_offspring_f",
          key: "predicted_offspring_f",
          width: 150,
          scopedSlots: {customRender: 'predicted_offspring_f'},
        },
        {
          title: "选配记录",
          dataIndex: "item_select_record_name",
          key: "item_select_record_name",
          scopedSlots: {customRender: 'item_select_record_name'}
        },
        {
          title: "操作人",
          dataIndex: "user.user_name",
          key: "operator",
        },
        {
          title: "创建时间",
          dataIndex: "created_at",
          key: "created_at",
        },
        {title: '操作', key: 'operation', scopedSlots: { customRender: 'operation' } }
      ]
    }
  },
  computed: {
    visibleColumns() {
      if (!this.useInbreedingAlgo) {
        return this.columns.filter(col => col.dataIndex !== 'predicted_offspring_f')
      }
      return this.columns
    }
  },
  async mounted() {
    this.refresh_list()
  },
  methods: {
    async refresh_list() {
      try {
        // 合并额外参数
        const finalQuery = {
          ...this.query,
          ...this.extraParams,
          // 近交模式开关传递给后端
          use_inbreeding_algo: this.useInbreedingAlgo ? 1 : 0
        };
        
        const res = await this.$api.get_yu_zhong_records(finalQuery);
        this.list = res.list;
        this.pagination.total = res.page.total;
        this.pagination.pageSize = res.page.page_size;
        this.pagination.current = res.page.page_no;
      } catch (error) {
        console.error('获取育种记录失败:', error);
        this.$message.error('获取数据失败');
      }
    },
    formatInbreeding(val) {
      if (val === null || val === undefined) return '待计算'
      return Number(val).toFixed(4)
    },
    getInbreedingColor(val) {
      if (val === null || val === undefined) return '#999'
      const v = Number(val)
      if (v <= 0.03125) return '#52c41a'   // 绿色 - 安全 (<= 3.125%)
      if (v <= 0.0625) return '#fa8c16'    // 橙色 - 警告 (3.125% < F <= 6.25%)
      return '#f5222d'                     // 红色 - 禁止 (> 6.25%)
    },
    getInbreedingLevel(val) {
      if (val === null || val === undefined) return 'unknown'
      const v = Number(val)
      if (v <= 0.03125) return 'safe'
      if (v <= 0.0625) return 'warning'
      return 'danger'
    },
    inbreedingRowClass(record) {
      if (!this.useInbreedingAlgo) return ''
      const level = this.getInbreedingLevel(record.predicted_offspring_f)
      if (level === 'danger') return 'inbreeding-danger-row'
      if (level === 'warning') return 'inbreeding-warning-row'
      return ''
    },
    handleTableChange(pagination, filters, sorter) {
      this.pagination.current = pagination.current
      this.query.page_no = pagination.current
      this.refresh_list()
    },
    handleItemAction(e, row) {
      if (e.key === 'remove') {
        this.removeRecord(row);
      } else if (e.key === 'add_offspring') {
        this.openAddOffspringDialog(row);
      }
    },
    // 打开添加后代弹窗
    openAddOffspringDialog(row) {
      // 构造预设的父母信息的个体数据
      const presetItem = {
        father_id: row.male_id,
        mother_id: row.female_id,
        item_father: row.male_vid,
        item_mother: row.female_vid,
        farm_id: row.farm_id
      };
      // 调用 addOrEdit 组件的 open 方法，传入预设的父母信息
      this.$refs.breedItemEdit.open('add', presetItem);
    },
    // 后代添加成功回调
    onOffspringAdded() {
      this.$message.success('后代添加成功');
      // 刷新列表
      this.refresh_list();
    },
    async removeRecord(row) {
      this.$confirm({
        title: '确认删除',
        content: `确定要删除这条育种记录吗？`,
        okText: '确认',
        cancelText: '取消',
        onOk: async () => {
          try {
            // 调用删除接口，使用育种记录的ID
            const res = await this.$api.delete_yu_zhong_record(row.id);
            if (res.code === 200) {
              this.$message.success('删除成功');
              // 刷新列表
              this.refresh_list();
            } else {
              this.$message.error(res.message || '删除失败');
            }
          } catch (error) {
            console.error('删除育种记录失败:', error);
            this.$message.error('删除失败');
          }
        }
      });
    },
    goToSelectRecord(id) {
      if (id) {
        this.$router.push(`/farms/cell/${id}?group_type=selectRecord`);
      }
    },
    onExpand(expanded, record) {
      this.$nextTick(() => {
        if (expanded) {
          this.$refs[`result-${record.male_vid}-${record.female_vid}`].draw(record.male_id, record.female_id)
        }
      })
    }
  },
}
</script>

<style scoped>
.select-record-tag {
  cursor: pointer;
  background: linear-gradient(135deg, #2563EB 0%, #3b82f6 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 2px 12px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
}
.select-record-tag:hover {
  background: linear-gradient(135deg, #1d4ed8 0%, #2563EB 100%);
  opacity: 0.9;
}

/* 近交警告行高亮 */
::v-deep .inbreeding-warning-row {
  background-color: #fffbe6 !important;
}
::v-deep .inbreeding-warning-row:hover > td {
  background-color: #fff1b8 !important;
}

/* 近交禁止行高亮 */
::v-deep .inbreeding-danger-row {
  background-color: #fff1f0 !important;
}
::v-deep .inbreeding-danger-row:hover > td {
  background-color: #ffccc7 !important;
}
</style>